import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { Sparkles, Heart, Layers, MessageCircleHeart, ShieldCheck, Lock, Eye } from "lucide-react";
import { API } from "@/lib/auth";
import { Footer } from "@/components/Footer";
import { ARCANE_BY_ID, CARD_BACK } from "@/data/tarot";
import { ShareButton } from "@/components/ShareButton";
import { usePageMeta } from "@/lib/seo";

const C = {
  bg: "#1C1C1E",
  title: "#F4EAD4",
  body: "#E5E5EA",
  mist: "#A2A2A6",
  goldMat: "#C5A059",
  goldLine: "#E6C687",
};

const STEPS = [
  { icon: MessageCircleHeart, title: "Pose ta question", text: "Ton prénom, celui de la personne concernée, et la question de cœur qui t'habite." },
  { icon: Layers, title: "Choisis tes 5 cartes", text: "Les 22 arcanes sont mélangés puis étalés face cachée. Ton intuition désigne ta Croix." },
  { icon: Heart, title: "Le Tarot parle", text: "Une lecture claire et sincère de ton tirage, reliée à ta question et à ton histoire." },
];

const TRUST = [
  { icon: Eye, title: "La vérité des cartes", text: "Aucune complaisance : le tirage est lu avec honnêteté, qu'il soit doux ou exigeant." },
  { icon: ShieldCheck, title: "Paiement sécurisé", text: "Ta consultation est protégée par un paiement sécurisé. Simple, rapide, sans abonnement." },
  { icon: Lock, title: "Discrétion totale", text: "Ta question et tes prénoms restent confidentiels. Un moment rien qu'à toi." },
];

// Trois arcanes lumineux pour l'aperçu du hero
const HERO_CARDS = [17, 6, 19];

export default function Landing() {
  usePageMeta({
    title: "La Petite Boîte Intuitive - Tarot de Marseille Gratuit en Ligne",
    description: "Tirage de Tarot de Marseille gratuit. Obtenez des réponses précises et claires à toutes vos questions sentimentales.",
    path: "/",
  });
  const navigate = useNavigate();
  const [price, setPrice] = useState({ amount: 5, currency: "eur", free_launch: true });
  const [showWelcome, setShowWelcome] = useState(true);

  useEffect(() => {
    axios.get(`${API}/price`).then(({ data }) => setPrice(data)).catch(() => {});
  }, []);

  const priceLabel = `${Number(price.amount).toFixed(0)} €`;
  const go = () => navigate("/tirage");

  return (
    <div className="relative min-h-screen flex flex-col" style={{ backgroundColor: C.bg }}>
      <div className="oracle-bg" />

      {/* ===================== MODALE DE BIENVENUE (LANCEMENT GRATUIT) ===================== */}
      <AnimatePresence>
        {showWelcome && (
          <motion.div
            data-testid="welcome-modal"
            className="fixed inset-0 z-50 flex items-center justify-center px-4 py-8"
            style={{ backgroundColor: "rgba(10,10,12,0.55)" }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
            onClick={() => setShowWelcome(false)}
          >
            <motion.div
              className="relative w-full max-w-lg max-h-[88vh] overflow-y-auto rounded-3xl p-8 sm:p-10"
              style={{
                background: "rgba(28,28,30,0.72)",
                backdropFilter: "blur(22px)",
                WebkitBackdropFilter: "blur(22px)",
                border: "1px solid rgba(230,198,135,0.28)",
                boxShadow: "0 24px 70px rgba(0,0,0,0.55), inset 0 1px 0 rgba(255,255,255,0.06)",
              }}
              initial={{ opacity: 0, y: 24, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 16, scale: 0.98 }}
              transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
              onClick={(e) => e.stopPropagation()}
            >
              <h2 className="font-display text-xl sm:text-2xl leading-snug text-center font-light" style={{ color: C.title }}>
                ✨ Bienvenue dans votre espace intuitif (Lancement Gratuit !) ✨
              </h2>
              <span className="block mx-auto my-6 h-px w-16" style={{ backgroundColor: "rgba(230,198,135,0.4)" }} />

              <p className="font-body text-[14px] sm:text-[15px] leading-relaxed" style={{ color: C.body }}>
                Bonjour ! J'ai créé La Petite Boîte Intuitive pour vous offrir une expérience de tirage du
                Tarot de Marseille fidèle à un tirage physique traditionnel. Pour fêter son lancement, j'ai
                choisi de vous ouvrir toutes les portes : l'application est 100 % gratuite en ce moment.
                Profitez-en pleinement !
              </p>

              <p className="font-body uppercase tracking-[0.14em] text-xs mt-6 mb-2" style={{ color: C.goldLine }}>
                La touche personnelle
              </p>
              <p className="font-body text-[14px] sm:text-[15px] leading-relaxed" style={{ color: C.body }}>
                Je voulais absolument fuir les tirages virtuels froids et robotiques. C'est pourquoi j'ai
                développé une mécanique unique : quand vous lancez le mélange, l'algorithme s'arrête et les
                cartes se figent réellement. La carte sur laquelle vous allez cliquer est la véritable carte !
                Ce n'est pas la machine qui décide au dernier moment : c'est
                votre propre intuition qui vous guide vers elle. Prenez un instant, respirez, et laissez
                l'énergie opérer.
              </p>

              <div className="mt-9 flex justify-center">
                <button
                  data-testid="welcome-cta"
                  onClick={() => { setShowWelcome(false); go(); }}
                  style={{ backgroundColor: C.goldMat, color: C.bg }}
                  className="inline-flex items-center gap-2 tracking-[0.12em] uppercase rounded-sm px-8 py-4 font-body text-sm transition-opacity hover:opacity-90"
                >
                  Ouvrir La Petite Boîte 💫
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="relative z-10 flex-1">
        {/* ===================== HERO ===================== */}
        <section className="max-w-5xl mx-auto px-6 pt-16 pb-20 sm:pt-24 text-center">
          <motion.p initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="font-body uppercase tracking-[0.35em] text-xs sm:text-sm" style={{ color: C.mist }}>
            Tarot de Marseille · Tirage en Croix
          </motion.p>

          <motion.h1 initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="font-display uppercase tracking-[0.06em] text-4xl sm:text-5xl lg:text-6xl leading-tight font-light mt-5" style={{ color: C.title }}>
            La Petite Boîte Intuitive
          </motion.h1>

          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }} className="flex items-center justify-center gap-3 mt-5">
            <span className="h-px w-8" style={{ backgroundColor: "rgba(230,198,135,0.4)" }} />
            <p className="font-body italic text-sm sm:text-base" style={{ color: C.goldLine }}>Tirage d'ordre sentimental · les questions de cœur</p>
            <span className="h-px w-8" style={{ backgroundColor: "rgba(230,198,135,0.4)" }} />
          </motion.div>

          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.25 }} className="font-body text-[11px] tracking-[0.14em] mt-3" style={{ color: C.mist, fontWeight: 300 }}>
            Propulsé par L'Observatoire des Possibles
          </motion.p>

          <motion.p initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="font-body text-base sm:text-lg leading-relaxed max-w-xl mx-auto mt-8" style={{ color: C.body }}>
            Une question de cœur t'habite ? Ouvre la Petite Boîte, choisis toi-même tes cartes,
            et reçois une lecture sincère de ton tirage en Croix — sans détour, sans faux espoirs.
          </motion.p>

          {/* Aperçu cartes */}
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }} className="flex items-center justify-center gap-3 sm:gap-5 mt-12">
            {HERO_CARDS.map((id, i) => (
              <motion.div
                key={id}
                initial={{ rotate: (i - 1) * 8, y: 0 }}
                animate={{ y: [0, -10, 0] }}
                transition={{ duration: 4, repeat: Infinity, delay: i * 0.5, ease: "easeInOut" }}
                style={{ rotate: `${(i - 1) * 8}deg` }}
                className="w-24 sm:w-32 aspect-[2/3] rounded-lg overflow-hidden border"
              >
                <div className="w-full h-full rounded-lg overflow-hidden border" style={{ borderColor: "rgba(230,198,135,0.45)", boxShadow: "0 0 22px rgba(230,198,135,0.25)" }}>
                  <img src={ARCANE_BY_ID[id].image} alt={ARCANE_BY_ID[id].name} className="w-full h-full object-cover" />
                </div>
              </motion.div>
            ))}
          </motion.div>

          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }} className="mt-12 flex flex-col items-center gap-4">
            <button data-testid="hero-cta" onClick={go} style={{ backgroundColor: C.goldMat, color: C.bg }} className="inline-flex items-center gap-2 tracking-[0.12em] uppercase rounded-sm px-9 py-4 font-body text-sm transition-opacity hover:opacity-90">
              <Sparkles size={16} />
              Ouvrir la petite boîte
            </button>
            <span data-testid="hero-price" className="font-body text-sm" style={{ color: C.mist }}>
              {price.free_launch ? "Offerte pour le lancement · profitez-en" : `Une consultation · ${priceLabel}`}
            </span>
          </motion.div>
        </section>

        {/* ===================== COMMENT ÇA MARCHE ===================== */}
        <section className="max-w-5xl mx-auto px-6 py-16 border-t" style={{ borderColor: "rgba(229,229,234,0.08)" }}>
          <h2 className="font-display uppercase tracking-[0.12em] text-2xl sm:text-3xl text-center font-light" style={{ color: C.title }}>Comment ça se passe</h2>
          <div className="grid sm:grid-cols-3 gap-6 mt-12">
            {STEPS.map((s, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.12 }} data-testid={`step-${i}`} className="rounded-2xl border p-7 text-center" style={{ borderColor: "rgba(230,198,135,0.18)", backgroundColor: "rgba(255,255,255,0.025)" }}>
                <div className="flex justify-center mb-4"><s.icon size={30} strokeWidth={1.3} style={{ color: C.goldLine }} /></div>
                <p className="font-body uppercase tracking-[0.12em] text-xs mb-3" style={{ color: C.goldLine }}>Étape {i + 1}</p>
                <h3 className="font-display text-xl mb-3" style={{ color: C.title }}>{s.title}</h3>
                <p className="font-body text-sm leading-relaxed" style={{ color: C.mist }}>{s.text}</p>
              </motion.div>
            ))}
          </div>
        </section>

        {/* ===================== CONFIANCE ===================== */}
        <section className="max-w-5xl mx-auto px-6 py-16 border-t" style={{ borderColor: "rgba(229,229,234,0.08)" }}>
          <h2 className="font-display uppercase tracking-[0.12em] text-2xl sm:text-3xl text-center font-light" style={{ color: C.title }}>Une lecture en qui tu peux avoir confiance</h2>
          <div className="grid sm:grid-cols-3 gap-6 mt-12">
            {TRUST.map((t, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.12 }} data-testid={`trust-${i}`} className="flex flex-col items-center text-center gap-3">
                <t.icon size={26} strokeWidth={1.3} style={{ color: C.goldLine }} />
                <h3 className="font-display text-lg" style={{ color: C.title }}>{t.title}</h3>
                <p className="font-body text-sm leading-relaxed max-w-xs" style={{ color: C.mist }}>{t.text}</p>
              </motion.div>
            ))}
          </div>
        </section>

        {/* ===================== TARIF + CTA FINAL ===================== */}
        <section className="max-w-2xl mx-auto px-6 py-16 text-center">
          <div className="rounded-2xl border p-10" style={{ borderColor: "rgba(230,198,135,0.3)", backgroundColor: "rgba(230,198,135,0.05)" }}>
            <p className="font-body uppercase tracking-[0.2em] text-xs" style={{ color: C.mist }}>Une consultation complète</p>
            <div className="flex items-end justify-center gap-2 my-4">
              {price.free_launch ? (
                <>
                  <span className="font-display text-5xl sm:text-6xl font-light" style={{ color: C.title }}>Gratuit</span>
                  <span className="font-body text-base line-through mb-2" style={{ color: C.mist }}>{Number(price.amount).toFixed(0)} €</span>
                </>
              ) : (
                <>
                  <span className="font-display text-6xl font-light" style={{ color: C.title }}>{Number(price.amount).toFixed(0)}</span>
                  <span className="font-display text-2xl mb-2" style={{ color: C.goldLine }}>€</span>
                </>
              )}
            </div>
            <p className="font-body text-sm leading-relaxed mb-8" style={{ color: C.body }}>
              {price.free_launch
                ? "Offre de lancement : ton tirage en Croix complet, 5 cartes choisies par toi, une interprétation experte et sincère — entièrement offert. Sans engagement."
                : "Un tirage en Croix complet, 5 cartes choisies par toi, une interprétation experte et sincère. Sans abonnement, sans engagement."}
            </p>
            <button data-testid="final-cta" onClick={go} style={{ backgroundColor: C.goldMat, color: C.bg }} className="inline-flex items-center gap-2 tracking-[0.12em] uppercase rounded-sm px-9 py-4 font-body text-sm transition-opacity hover:opacity-90">
              <Sparkles size={16} />
              Ouvrir la petite boîte
            </button>
          </div>

          <div className="mt-10 flex justify-center">
            <ShareButton testid="share-landing" />
          </div>
        </section>
      </div>

      <Footer />
    </div>
  );
}
